import posthog from 'posthog-js'
import i18n from 'i18next'
import axiosInstance from '../../axios/axiosInstance'
import store from '../../redux/store'
import { POCKET_ADD, POCKET_PENDING, POCKET_REMOVE, SET_POCKET } from '../../redux/types'
import { POCKET_LIMIT, PocketWord } from '../../redux/reducers/pocketReducer'
import { wordClick } from '../../redux/actions/storyAction'
import { setWordMastery } from '../../redux/actions/wordsAction'
import { drawUnderlines, syncPocketRules, undrawUnderlines } from './pocketUnderline'
import type { ReleaseMode } from './cardHolderView'

/** What the pocket component exposes to the controller once it is on screen. */
export interface PocketSurface {
  fly: (word: string, origin: Element | null, wordsAfter: string[]) => Promise<void>
  markFresh: (word: string) => void
  release: (word: string, mode: ReleaseMode) => Promise<void>
  openSwap: (request: SwapRequest) => void
  toast: (message: string) => void
}

export interface SwapRequest {
  entry: PocketWord
  source: Element | null
}

let surface: PocketSurface | null = null
export const registerPocketSurface = (s: PocketSurface | null) => {
  surface = s
}

const track = (event: string, props: Record<string, any>) => {
  try {
    posthog.capture(event, { surface: 'web_reader', path: window.location.pathname, ...props })
  } catch {
    /* analytics must never break the pocket */
  }
}

const pocketState = () => store.getState().pocket

export const isPocketed = (word: string) =>
  pocketState().words.some((w) => w.word === word) || pocketState().pending.includes(word)

export const isPocketFull = () => pocketState().words.length + pocketState().pending.length >= POCKET_LIMIT

// Set once this browser has seen a word in the pocket, so "Pocket it" stops glowing for good.
const POCKET_USED_KEY = 'word_pocket_used'
export const hasUsedPocket = () => {
  try {
    return localStorage.getItem(POCKET_USED_KEY) === '1'
  } catch {
    return false
  }
}
const markPocketUsed = () => {
  try {
    localStorage.setItem(POCKET_USED_KEY, '1')
  } catch {
    /* private mode: the glow just comes back on an empty pocket */
  }
}

let loading: Promise<void> | null = null
export const loadPocket = () => {
  if (pocketState().loaded || loading) return loading
  loading = axiosInstance
    .get('word-pocket')
    .then((res) => {
      const words = res?.data?.words || []
      if (words.length) markPocketUsed()
      store.dispatch({ type: SET_POCKET, payload: words })
      syncPocketRules()
    })
    .catch(() => {
      /* the pocket stays empty; the next page load tries again */
    })
    .finally(() => {
      loading = null
    })
  return loading
}

const setPending = (word: string, on: boolean) => store.dispatch({ type: POCKET_PENDING, payload: { word, on } })

/**
 * Pockets a word. The characters fly at once and the save runs alongside the flight; if the
 * save fails the word comes back out and the learner is told why. A full pocket opens the swap view.
 */
export async function pocketWord(entry: PocketWord, origin: Element | null, source: Element | null) {
  const { word } = entry
  if (!word || isPocketed(word)) return
  if (isPocketFull()) {
    surface?.openSwap({ entry, source })
    return
  }

  setPending(word, true)
  const save = axiosInstance.post('word-pocket', {
    word,
    languageDictionaryId: entry.languageDictionaryId || undefined,
    pinyin: entry.pinyin,
    meaning: entry.meaning,
    hasAudio: entry.hasAudio,
    sentenceWords: entry.sentenceWords,
    sentenceEn: entry.sentenceEn || undefined,
    storyId: entry.storyId || undefined,
  })
  // Keep the outcome around without letting an early rejection go unhandled mid-flight.
  const outcome = save.then(
    () => ({ ok: true as const }),
    (error) => ({ ok: false as const, full: error?.response?.status === 409 }),
  )

  const wordsAfter = [...pocketState().words.map((w) => w.word), word]
  if (surface) await surface.fly(word, origin, wordsAfter)
  surface?.markFresh(word)
  store.dispatch({ type: POCKET_ADD, payload: { ...entry, createdAt: new Date().toISOString() } })
  setPending(word, false)
  drawUnderlines(word, source)
  track('word_pocketed', { word, pocket_size: pocketState().words.length })

  const result = await outcome
  if (result.ok) {
    markPocketUsed()
    return
  }
  await takeOutLocally(word, 'fail')
  surface?.toast(
    result.full ? i18n.t('reader:pocket.fullToast') : i18n.t('reader:pocket.pocketFailed', { word }),
  )
}

async function takeOutLocally(word: string, mode: ReleaseMode) {
  await Promise.all([surface?.release(word, mode), undrawUnderlines(word)])
  store.dispatch({ type: POCKET_REMOVE, payload: word })
}

/** Takes a word out. Its row is deleted; "mastered" also marks the word as mastered. */
export async function takeOut(word: string, mode: 'remove' | 'mastered') {
  const entry = pocketState().words.find((w) => w.word === word)
  if (!entry) return
  const request = axiosInstance.delete(`word-pocket/${encodeURIComponent(word)}`)
  if (mode === 'mastered') markMastered(entry)
  track('word_unpocketed', { word, reason: mode, pocket_size: pocketState().words.length - 1 })
  await takeOutLocally(word, mode)
  try {
    await request
  } catch {
    surface?.markFresh(word)
    store.dispatch({ type: POCKET_ADD, payload: entry })
    syncPocketRules()
    surface?.toast(i18n.t('reader:pocket.takeOutFailed', { word }))
  }
}

function markMastered(entry: PocketWord) {
  const story = store.getState().story
  const storyId = entry.storyId || story?.currentStory?.id
  if (storyId) {
    // Same call as the word panel's "Mark as mastered", so the reader's underline updates too.
    wordClick({
      word: entry.word,
      word_status: 'known',
      storyId,
      mastery: story?.storyDictionary?.[entry.word]?.mastery as string,
    })
  } else if (entry.languageDictionaryId) {
    setWordMastery(entry.languageDictionaryId, 'mastered')
  }
}

/** Swaps a word out to make room, then flies the waiting word in. */
export async function swapInto(outWord: string, request: SwapRequest, origin: Element | null) {
  await takeOut(outWord, 'remove')
  await pocketWord(request.entry, origin, request.source)
}

export const trackPocketOpened = () => track('word_pocket_opened', { pocket_size: pocketState().words.length })
